import { Flow } from '@minigame-race/contracts';
import { useMemo, useRef, useState } from 'react';
import type { MinigameProps } from '../../race/types.js';

/**
 * Flow. Press on a dot and drag to trace a path to its twin. The path commits
 * automatically the moment it reaches the matching dot, exactly like the real
 * game — you don't have to release on the precise pixel.
 *
 * Interaction notes (these were the bugs in the first version):
 *  - Pointer capture goes on the BOARD, not the cell you happened to press, so
 *    every subsequent move routes here even as the finger leaves the first cell.
 *  - The in-progress path lives in a ref, so the commit reads the true latest
 *    path rather than a stale React state snapshot.
 */

function isView(v: unknown): v is Flow.FlowPrivateView {
  return typeof v === 'object' && v !== null && (v as { kind?: string }).kind === 'flow';
}

interface Draft {
  color: number;
  cells: number[];
}

export function FlowGame({ view, emit }: MinigameProps) {
  const s = isView(view) ? view : null;
  const boardRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef<Draft | null>(null);
  // A version counter to force re-render as the ref mutates (we render from the ref).
  const [, bump] = useState(0);
  const rerender = (): void => bump((n) => n + 1);

  const size = s?.size ?? 0;

  const endpointByCell = useMemo(() => {
    const m = new Map<number, Flow.FlowEndpoints>();
    for (const e of s?.endpoints ?? []) {
      m.set(e.a, e);
      m.set(e.b, e);
    }
    return m;
  }, [s?.endpoints]);

  if (!s) return null;

  const draft = draftRef.current;

  const cellFromPointer = (clientX: number, clientY: number): number | null => {
    const el = boardRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) return null;
    const c = Math.min(size - 1, Math.max(0, Math.floor((x / rect.width) * size)));
    const r = Math.min(size - 1, Math.max(0, Math.floor((y / rect.height) * size)));
    return r * size + c;
  };

  const adjacent = (a: number, b: number): boolean => {
    const ar = Math.floor(a / size), ac = a % size;
    const br = Math.floor(b / size), bc = b % size;
    return Math.abs(ar - br) + Math.abs(ac - bc) === 1;
  };

  const commit = (d: Draft): void => {
    const ep = s.endpoints.find((e) => e.color === d.color);
    if (!ep) return;
    const first = d.cells[0];
    const last = d.cells[d.cells.length - 1];
    const complete = d.cells.length >= 2 && ((first === ep.a && last === ep.b) || (first === ep.b && last === ep.a));
    // Commit a completed path; otherwise clear the colour so a half-drag doesn't
    // leave a dead stub on the board.
    emit({ type: 'path', color: d.color, cells: complete ? d.cells : [] });
  };

  const onDown = (e: React.PointerEvent): void => {
    if (s.isComplete) return;
    boardRef.current?.setPointerCapture(e.pointerId);
    const cell = cellFromPointer(e.clientX, e.clientY);
    if (cell === null) return;
    const ep = endpointByCell.get(cell);
    if (!ep) return;
    // Starting a colour clears its previous path (server echoes the clear).
    draftRef.current = { color: ep.color, cells: [cell] };
    rerender();
  };

  const onMove = (e: React.PointerEvent): void => {
    const d = draftRef.current;
    if (!d) return;
    const cell = cellFromPointer(e.clientX, e.clientY);
    if (cell === null) return;
    const cells = d.cells;
    const lastCell = cells[cells.length - 1];
    if (lastCell === undefined || cell === lastCell) return;

    // Backtrack onto the previous cell.
    if (cells.length >= 2 && cells[cells.length - 2] === cell) {
      d.cells = cells.slice(0, -1);
      rerender();
      return;
    }
    if (!adjacent(lastCell, cell)) return;
    if (cells.includes(cell)) return; // no self-crossing
    const ep = endpointByCell.get(cell);
    if (ep && ep.color !== d.color) return; // can't cross another colour's dot

    d.cells = [...cells, cell];

    // Auto-complete: reaching the matching endpoint commits immediately.
    const own = s.endpoints.find((x) => x.color === d.color);
    if (own) {
      const other = cell === own.a ? own.b : cell === own.b ? own.a : null;
      const startedAt = d.cells[0];
      if (other !== null && startedAt !== undefined && startedAt !== cell && (startedAt === own.a || startedAt === own.b)) {
        commit(d);
        draftRef.current = null;
        boardRef.current?.releasePointerCapture?.(e.pointerId);
        rerender();
        return;
      }
    }
    rerender();
  };

  const onUp = (e: React.PointerEvent): void => {
    const d = draftRef.current;
    draftRef.current = null;
    boardRef.current?.releasePointerCapture?.(e.pointerId);
    if (d) commit(d);
    rerender();
  };

  // Every committed path, plus the live draft, as {color, cells}.
  const allPaths: { color: number; cells: number[] }[] = [
    ...Object.entries(s.paths).map(([c, cells]) => ({ color: Number(c), cells: [...cells] })),
  ];
  if (draft && draft.cells.length >= 2) {
    // The draft supersedes any committed path of the same colour while drawing.
    const i = allPaths.findIndex((p) => p.color === draft.color);
    if (i >= 0) allPaths.splice(i, 1);
    allPaths.push({ color: draft.color, cells: draft.cells });
  }

  // SVG viewBox is `size` units square; a cell centre is (col+0.5, row+0.5).
  const cx = (cell: number): number => (cell % size) + 0.5;
  const cy = (cell: number): number => Math.floor(cell / size) + 0.5;
  const lineW = 0.62; // in cell units — a fat rounded pipe

  return (
    <div style={{ width: '100%', maxWidth: 'min(88cqmin,420px)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 'clamp(8px,2cqmin,14px)' }}>
      <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', letterSpacing: '.14em', color: 'rgba(234,255,234,.5)' }}>
        {s.filledCells}/{s.totalCells} FILLED · {s.connectedColors}/{s.totalColors} JOINED
      </div>

      <div
        ref={boardRef}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          padding: 'clamp(4px,1cqmin,8px)',
          borderRadius: 12,
          background: 'rgba(0,0,0,.35)',
          border: '1px solid rgba(166,255,46,.14)',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        {/* faint grid lines so the cells are still readable under the pipes */}
        <div
          style={{
            position: 'absolute',
            inset: 'clamp(4px,1cqmin,8px)',
            display: 'grid',
            gridTemplateColumns: `repeat(${size}, 1fr)`,
            gridTemplateRows: `repeat(${size}, 1fr)`,
            gap: 1,
          }}
          aria-hidden
        >
          {Array.from({ length: size * size }, (_, i) => (
            <div key={i} style={{ borderRadius: 3, background: 'rgba(255,255,255,.035)' }} />
          ))}
        </div>

        {/* the pipes + endpoint dots, drawn as vector so they scale crisply */}
        <svg
          viewBox={`0 0 ${size} ${size}`}
          preserveAspectRatio="none"
          style={{ position: 'absolute', inset: 'clamp(4px,1cqmin,8px)', pointerEvents: 'none' }}
        >
          {allPaths.map((p) => {
            if (p.cells.length < 2) return null;
            const d = p.cells.map((c, i) => `${i === 0 ? 'M' : 'L'} ${cx(c)} ${cy(c)}`).join(' ');
            return (
              <path
                key={p.color}
                d={d}
                fill="none"
                stroke={Flow.FLOW_PALETTE[p.color]}
                strokeWidth={lineW}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={0.92}
              />
            );
          })}
          {s.endpoints.map((ep) => (
            <g key={ep.color}>
              <circle cx={cx(ep.a)} cy={cy(ep.a)} r={0.34} fill={Flow.FLOW_PALETTE[ep.color]} />
              <circle cx={cx(ep.b)} cy={cy(ep.b)} r={0.34} fill={Flow.FLOW_PALETTE[ep.color]} />
            </g>
          ))}
        </svg>
      </div>

      <div style={{ textAlign: 'center', fontSize: 'clamp(9px,1.7cqmin,12px)', color: 'rgba(234,255,234,.4)' }}>
        Drag from a dot to its match. Fill every cell.
      </div>
    </div>
  );
}
